// ./src/utils/api/ArtistService.ts
//
// Artist service — the account-level library of bookable acts (bands, DJs, solo
// performers, speakers, comedians…) reused across events. OpResult-native. Managed
// from the account Library page OR (with a `contextEventId` AUTH CONTEXT) from an
// event so crew with event.operate on that event can manage the event account's
// roster. Mirrors InventoryItemService — swap the entity, the nested `barcodes`
// rows become nested `members` rows. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/artist.ts)
// =============================================================================

export const ARTIST_KIND = {
  SOLO: "solo",
  BAND: "band",
  DUO: "duo",
  DJ: "dj",
  COMEDIAN: "comedian",
  SPEAKER: "speaker",
  PANEL: "panel",
  OTHER: "other",
} as const;

export type ArtistKind = (typeof ARTIST_KIND)[keyof typeof ARTIST_KIND];

export const ARTIST_KIND_VALUES = Object.values(ARTIST_KIND);

export const ARTIST_KIND_LABELS: Record<string, string> = {
  solo: "Solo",
  band: "Band",
  duo: "Duo",
  dj: "DJ",
  comedian: "Comedian",
  speaker: "Speaker",
  panel: "Panel",
  other: "Other",
};

/** Kinds that imply more than one performer — the members section is most useful here. */
export const ARTIST_GROUP_KINDS: string[] = [ARTIST_KIND.BAND, ARTIST_KIND.DUO, ARTIST_KIND.PANEL];

export const ARTIST_DEAL_TYPE = {
  NONE: "none",
  FLAT_GUARANTEE: "flat_guarantee",
  DOOR_SPLIT: "door_split",
  GUARANTEE_VS_PERCENTAGE: "guarantee_vs_percentage",
} as const;

export type ArtistDealType = (typeof ARTIST_DEAL_TYPE)[keyof typeof ARTIST_DEAL_TYPE];

export const ARTIST_DEAL_TYPE_VALUES = Object.values(ARTIST_DEAL_TYPE);

export const ARTIST_DEAL_TYPE_LABELS: Record<string, string> = {
  none: "None",
  flat_guarantee: "Flat guarantee",
  door_split: "Door split",
  guarantee_vs_percentage: "Guarantee vs. percentage",
};

/** One person in an act's line-up (a band member, a panelist, a soloist). */
export interface ArtistMember {
  id?: string;
  /** Soft link → a contact (person). Null when not linked. */
  personId?: string | null;
  nameTitle?: string | null;
  /** OVERRIDE name — blank when linked = use the contact's current name. Bind editor inputs here. */
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  avatarUploadedFileId?: string | null;
  /** Effective (override ?? linked contact's CURRENT value) — use for DISPLAY. Read-only. */
  displayFirstName?: string | null;
  displayLastName?: string | null;
  displayEmail?: string | null;
  displayPhone?: string | null;
  displayAvatarUploadedFileId?: string | null;
  /** Instrument / role text (e.g. "Lead vocals", "Keys", "MC"). */
  memberRole?: string | null;
  /** A core member (as opposed to a substitute / guest). */
  isCore?: boolean | null;
  orderNo?: number | null;
}

// =============================================================================
// GraphQL
// =============================================================================

const ARTIST_MEMBER_FIELDS = `
  id
  personId
  nameTitle
  firstName
  lastName
  email
  phone
  avatarUploadedFileId
  displayFirstName
  displayLastName
  displayEmail
  displayPhone
  displayAvatarUploadedFileId
  memberRole
  isCore
  orderNo
`;

const ARTIST_FIELDS = `
  id
  accountId
  name
  kind
  isGroup
  categoryId
  summary
  description
  mainImageUploadedFileId
  mainImageUploadedFile {
    id
    fileUrl
    thumbnailUrl
  }
  website
  spotifyUrl
  youtubeUrl
  socialUrl1
  socialUrl2
  socialUrl3
  agentName
  agentCompany
  agentEmail
  agentPhone
  setLengthMin
  dealType
  defaultAmount
  currencyCode
  rider
  notes
  orderNo
  status
  rights
  createdAt
  updatedAt
  members { ${ARTIST_MEMBER_FIELDS} }
`;

const ARTIST_LIST_QUERY = gql`
  query ArtistList($filter: ArtistFilter, $params: PaginationAndSorting) {
    artistList(filter: $filter, params: $params) {
      code
      total
      data { ${ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ARTIST_GET_QUERY = gql`
  query ArtistGet($id: ID) {
    artistGet(id: $id) {
      code
      data { ${ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ARTIST_GET_MANY_QUERY = gql`
  query ArtistGetMany($ids: [ID]) {
    artistGetMany(ids: $ids) {
      code
      data { ${ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ARTIST_CREATE_MUTATION = gql`
  mutation ArtistCreate($params: ArtistInput) {
    artistCreate(params: $params) {
      code
      data { ${ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ARTIST_UPDATE_MUTATION = gql`
  mutation ArtistUpdate($where: ArtistWhereInput, $params: ArtistInput) {
    artistUpdate(where: $where, params: $params) {
      code
      data { ${ARTIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ARTIST_REMOVE_MUTATION = gql`
  mutation ArtistRemove($where: ArtistWhereInput) {
    artistRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Artist schema)
// =============================================================================

export interface Artist {
  id?: string;
  accountId?: string | null;
  name?: string | null;
  kind?: string | null;
  isGroup?: boolean | null;
  categoryId?: string | null;
  summary?: string | null;
  description?: string | null;
  mainImageUploadedFileId?: string | null;
  website?: string | null;
  spotifyUrl?: string | null;
  youtubeUrl?: string | null;
  socialUrl1?: string | null;
  socialUrl2?: string | null;
  socialUrl3?: string | null;
  agentName?: string | null;
  agentCompany?: string | null;
  agentEmail?: string | null;
  agentPhone?: string | null;
  setLengthMin?: number | null;
  dealType?: string | null;
  defaultAmount?: number | null;
  currencyCode?: string | null;
  rider?: string | null;
  notes?: string | null;
  /** Resolved promo photo (via federation) — url for the edit-load preview. */
  mainImageUploadedFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  members?: ArtistMember[] | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ArtistInput {
  /** Auth context (NOT stored): the event page a crew member is acting from. */
  contextEventId?: string | null;
  name?: string | null;
  kind?: string | null;
  isGroup?: boolean | null;
  categoryId?: string | null;
  summary?: string | null;
  description?: string | null;
  mainImageUploadedFileId?: string | null;
  website?: string | null;
  spotifyUrl?: string | null;
  youtubeUrl?: string | null;
  socialUrl1?: string | null;
  socialUrl2?: string | null;
  socialUrl3?: string | null;
  agentName?: string | null;
  agentCompany?: string | null;
  agentEmail?: string | null;
  agentPhone?: string | null;
  setLengthMin?: number | null;
  dealType?: string | null;
  defaultAmount?: number | null;
  currencyCode?: string | null;
  rider?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
  /** Full desired set, diffed on id server-side; omit to leave untouched. */
  members?: ArtistMemberInput[] | null;
}

export interface ArtistMemberInput {
  id?: string;
  personId?: string | null;
  nameTitle?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  avatarUploadedFileId?: string | null;
  memberRole?: string | null;
  isCore?: boolean | null;
}

export interface ArtistFilter {
  id?: string[] | null;
  categoryId?: string[] | null;
  kind?: string[] | null;
  isGroup?: boolean | null;
  status?: number[] | null;
  searchKeyword?: string | null;
  /** Auth/scoping context (NOT a column): the event page the list is shown on. */
  contextEventId?: string | null;
}

export interface ArtistWhereInput {
  id?: string | string[];
  contextEventId?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class ArtistService {
  static async list(filter?: ArtistFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(ARTIST_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Fetches EVERY artist matching `filter` by paging through the list — for whole-set
   * consumers (export) that must cover the full filtered set, not one page.
   */
  static async listAll(
    filter?: ArtistFilter,
    sortBy?: SortItem[],
    opts?: ListAllOptions
  ): Promise<OpResult> {
    return listAllPages<Artist>(ArtistService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(ARTIST_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(ARTIST_GET_MANY_QUERY, { ids });
  }

  static async create(params: ArtistInput): Promise<OpResult> {
    return ApiService.executeMutation(ARTIST_CREATE_MUTATION, { params });
  }

  static async update(where: ArtistWhereInput, params: ArtistInput): Promise<OpResult> {
    return ApiService.executeMutation(ARTIST_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string, contextEventId?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(ARTIST_REMOVE_MUTATION, {
      where: { id, contextEventId: contextEventId || undefined },
    });
  }

  /** Remove every selected artist in one call. */
  static async removeMany(ids: string[], contextEventId?: string | null): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(ARTIST_REMOVE_MUTATION, {
      where: { id: ids, contextEventId: contextEventId || undefined },
    });
  }
}
